/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { FormComboBoxComponent } from '@src/app/shared/form/form-combo-box/form-combo-box.component';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { maxChars, minWords, required } from '@src/app/shared/validators';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { MyRequestService } from '@src/app/modules/crm/my-request.service';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CtlHolderComponent } from '@src/app/shared/form/ctl-holder/ctl-holder.component';
import { MyUnitService } from '@src/app/modules/unit/my-unit.service';
import { Unit } from '@src/app/model/unit';

/**
 * MyRequest page uses this to create requests, when I forget name, handy to mention myRequest for search.
 *
 * Improved: the category picker is gone. It asked the owner to file their own problem under a
 * taxonomy built for the agency, and every branch of it ended on this same form. srTopic / srClass
 * are now worked out server side from what they actually wrote - see RequestQualifyService.
 */
@Component({
    selector: 'app-new-user-request-detail',
    standalone: true,
    templateUrl: './new-user-request-detail.component.html',
    styleUrls: ['./new-user-request-detail.component.scss'],
    imports: [CtlHolderComponent, MatButtonModule, MatProgressSpinnerModule]
})
export class NewUserRequestDetailComponent {
    private requestSvc = inject(MyRequestService);
    private cds = inject(ConfirmDialogService);
    private currentUserSvc = inject(CurrentUserService);

    @Input()
    existingList: ServiceRequest[] = [];

    @Output()
    selectRequest = new EventEmitter<ServiceRequest>();

    /** The request is qualified before the server answers, so the wait is longer than a normal save. */
    submitting = false;

    myUnits: Unit[] = [];
    unitField = FormComboBoxComponent.make('Unit', 'unitId', null, {
        items: this.myUnits, optionDisplayValue: (u: Unit) => u ? `${u.address} (${u.name})` : ''
    }, { hint: 'Leave blank if this is not about a particular unit' });
    unitCtl = this.unitField.makeControl();

    titleCtl = FormTextComponent.make('Name for this request', 'title',
        {hint: 'Short name to refer to this request by. Think of this like the subject of an email',
            validators: [required, minWords(3), maxChars(100)]
        }).makeControl();

    descriptionCtl = FormTextAreaComponent.make('Description of what needs doing', 'description',
        {validators: [ required, minWords(3), maxChars(512) ]}
    ).makeControl();

    constructor() {
        const myUnitSvc = inject(MyUnitService);

        myUnitSvc.get<Unit>().subscribe( units => {
            this.myUnits.length = 0;
            units.forEach ( u => this.myUnits.push(u))
            if (units.length === 1) {
                this.unitCtl.setValue(units[0].id);
                this.unitField.setComboDisplayValue(units[0]);
            }
        });
        this.resetForm();
    }

    resetForm() {
        this.titleCtl.setValue(null);
        this.descriptionCtl.setValue(null);
    }

    validate() {
        let isValid = true;
        [this.titleCtl, this.descriptionCtl].forEach( ctl => {
            if (ctl.invalid) {
                ctl.markAsDirty();
                ctl.markAsTouched();
                isValid = false;
            }
        });
        return isValid;
    }

    postNewRequest() {
        if (!this.validate() || this.submitting) {
            return;
        }
        const msg = $localize `Create this new request now?`;
        this.cds.open('Create New Request', msg, () => this.post());
    }

    private post() {
        this.submitting = true;
        const sr = this.getRequestDtls();
        this.requestSvc.postAgentRequest(sr, sr.teamId).subscribe({
            next: srSaved => {
                this.submitting = false;
                if (srSaved) {
                    this.existingList.push(srSaved);
                    this.selectRequest.emit(srSaved);
                    this.resetForm();
                }
            },
            error: () => { this.submitting = false; }
        });
    }

    /**
     * omcId and agentTeamId are deliberately not sent - the server derives both from teamId's
     * primary agent, and rejects the request if a caller names a different one.
     */
    getRequestDtls() {
        const sr = new ServiceRequest();
        const cu = this.currentUserSvc.currentUser;
        sr.teamId = cu.currentTeam.id;
        sr.reportedByPersonId = cu.currentProfileId;
        sr.personId = sr.reportedByPersonId;
        sr.unitId = this.unitCtl.value;
        sr.title = this.titleCtl.value;
        sr.description = this.descriptionCtl.value;
        sr.requestSource = ServiceRequest.srcApp.id;
        return sr;
    }
}
